import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface ConnectionSuggestion {
  id: string;
  full_name: string;
  profile_picture_url?: string;
  username?: string;
  academy_name?: string;
  academy_city?: string;
  academy_state?: string;
  country?: string;
  belt_rank?: string;
}

export const useConnectionSuggestions = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['connection-suggestions', user?.id],
    queryFn: async (): Promise<ConnectionSuggestion[]> => {
      if (!user) return [];

      try {
        // First, get all users who are not the current user
        const { data: allProfiles, error: profilesError } = await supabase
          .from('profiles')
          .select(`
            id,
            full_name,
            profile_picture_url,
            username,
            country,
            main_academy_id,
            belt_rank
          `)
          .neq('id', user.id)
          .limit(20);

        if (profilesError) {
          console.error('Error fetching profiles:', profilesError);
          throw profilesError;
        }

        // Then, get existing connections to filter them out
        const { data: existingConnections, error: connectionsError } = await supabase
          .from('connections')
          .select('requester, recipient, status')
          .or(`requester.eq.${user.id},recipient.eq.${user.id}`);

        if (connectionsError) {
          console.error('Error fetching connections:', connectionsError);
          throw connectionsError;
        }

        // Create a set of user IDs that are already connected or have pending requests
        const connectedUserIds = new Set<string>();
        existingConnections?.forEach(conn => {
          if (conn.requester === user.id) {
            // Se eu enviei a solicitação, não mostrar o usuário
            connectedUserIds.add(conn.recipient);
          } else if (conn.recipient === user.id) {
            // Se eu recebi a solicitação, não mostrar o usuário
            connectedUserIds.add(conn.requester);
          }
        });

        // Filter out already connected users
        const availableProfiles = allProfiles?.filter(profile => 
          !connectedUserIds.has(profile.id)
        ) || [];

        // Get academy information for the available profiles
        const profilesWithAcademies = await Promise.all(
          availableProfiles.slice(0, 10).map(async (profile) => {
            let academyInfo = {};
            
            if (profile.main_academy_id) {
              const { data: academy } = await supabase
                .from('academies')
                .select('name, city, state')
                .eq('id', profile.main_academy_id)
                .single();
              
              if (academy) {
                academyInfo = {
                  academy_name: academy.name,
                  academy_city: academy.city,
                  academy_state: academy.state,
                };
              }
            }

            return {
              id: profile.id,
              full_name: profile.full_name || 'Anonymous',
              profile_picture_url: profile.profile_picture_url,
              username: profile.username,
              country: profile.country,
              ...academyInfo,
              belt_rank: profile.belt_rank || 'white',
            } as ConnectionSuggestion;
          })
        );

        return profilesWithAcademies;
      } catch (error) {
        console.error('Error in connection suggestions:', error);
        throw error;
      }
    },
    enabled: !!user,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};
